// src/features/data/groups-stats-strip.ts
//
// IR-1228 r7 (Harel, 2026-09-14): "why the section of the list is so small and
// there is vertical scroll that allows to scroll out of the screen?"
//
// The Groups tab is locked to the viewport so the table's sticky header has a
// scrollport that never leaves the screen (IR-1228 r5, Rotem). That lock is
// correct, but it makes vertical space a fixed budget, and the statistics strip
// was spending ~340px of it — leaving roughly three rows of list on an 830px
// viewport. The strip now collapses, and the preference is remembered.
//
// Two helpers live here rather than inline in the tab so they can be tested
// without mounting the whole 2,500-line component.

const STORAGE_KEY = "iris-ops.groups.statsCollapsed";

/**
 * IR-1610 r2 (Rotem, 2026-09-22): the Avatars tab collapses its KPI strip the
 * same way, and remembers its own choice. One key per tab — an operator who
 * hides the Groups numbers has said nothing about the Avatars ones — so the
 * three helpers below take the key as a parameter instead of growing a second
 * copy of the try/catch. Existing callers pass nothing and get the Groups key.
 */
export const AVATARS_STATS_STORAGE_KEY = "iris-ops.avatars.statsCollapsed";

/** Anything with the two localStorage methods we use — lets tests pass a stub. */
export type ReadableStorage = Pick<Storage, "getItem" | "setItem">;

/**
 * The operator's own choice, or `null` if they have never made one.
 *
 * Storage can throw outright (Safari private mode, blocked site data), so every
 * read is guarded; an unreadable preference reads as "no choice made" rather
 * than taking the tab down with it.
 */
export function storedStatsPreference(
  storage: ReadableStorage = localStorage,
  key: string = STORAGE_KEY,
): boolean | null {
  try {
    const raw = storage.getItem(key);
    return raw === null ? null : raw === "1";
  } catch {
    return null;
  }
}

/**
 * Read the remembered preference, defaulting to EXPANDED.
 *
 * An operator who has never touched the toggle should see the numbers — the
 * strip is the reason most people open this tab, and a screen that silently
 * hides its own headline reads as broken.
 */
export function loadStatsCollapsed(
  storage: ReadableStorage = localStorage,
  key: string = STORAGE_KEY,
): boolean {
  return storedStatsPreference(storage, key) ?? false;
}

/** Persist the preference. A failed write must never break the toggle itself. */
export function saveStatsCollapsed(
  collapsed: boolean,
  storage: ReadableStorage = localStorage,
  key: string = STORAGE_KEY,
): void {
  try {
    storage.setItem(key, collapsed ? "1" : "0");
  } catch {
    // Ignored on purpose — the toggle still works for this session.
  }
}

/** Never lock the tab shorter than this, however cramped the window. */
export const MIN_LOCK_HEIGHT = 320;

/** Breathing room under the pagination row, inside the locked area. */
export const LOCK_GUTTER = 12;

/**
 * The table's floor: roughly six 47px rows plus the header.
 *
 * 🪤 WHY A FLOOR EXISTS AT ALL (Harel, 2026-09-14: "looks bad!"). The first
 * version of this lock spent the window on whatever came first and handed the
 * table the remainder. With the statistics strip expanded that remainder was
 * **one row** — a 2,020-row list rendered as a single line. Filling the screen
 * is not the goal; showing the data is. When the window genuinely cannot hold
 * the strip and a usable list, the tab is allowed to be taller than the window
 * and the page scrolls a little, which is ordinary and expected. Starving the
 * list to keep a scrollbar away is not.
 */
export const MIN_TABLE_HEIGHT = 320;

/**
 * Height for the viewport-locked tab root.
 *
 * 🪤 THIS REPLACES A MAGIC NUMBER THAT HAS NOW ROTTED TWICE. The tab root was
 * `calc(100vh - 130px)`, and before that `calc(100vh - 220px)`. Measured on
 * production, the real distance from the top of the window to the top of the
 * tab is **237px** (app header 56 + page padding 32 + title 34+20 + tab chips
 * 31+20) and the page adds **96px** of bottom padding under it. So the root was
 * overshooting the window by ~200px, the app shell scrolled to reach it, and
 * the list was reading as "small" only because part of it sat below the fold.
 *
 * The result is the LARGER of two numbers, and which one wins matters:
 *
 *   * `available` wins on a roomy window — the tab ends exactly on the fold, so
 *     there is no band of empty page underneath it.
 *   * `needed` wins on a cramped one — the tab is taller than the window, the
 *     page scrolls by the difference, and the list keeps its floor.
 *
 * Taking the larger is what removes the dead band in both directions: too small
 * a value leaves unused page below the tab, too large a value pushes the list
 * under the fold.
 *
 * @param top      the tab root's distance from the top of the CONTENT, not the
 *                 window — i.e. already corrected for how far the page is
 *                 scrolled. Measuring viewport coords while scrolled would
 *                 return a too-small `top` and hand back a too-tall lock, which
 *                 is the original bug wearing a different hat.
 * @param bottomPadding  the page's own padding below the tab, which occupies
 *                 window space the tab cannot use.
 * @param chromeHeight  everything inside the tab that is NOT the table — the
 *                 statistics strip, the filter bar, the pagination row. Measured
 *                 live (root height minus table height) rather than listed, so
 *                 collapsing the strip or wrapping the filters is accounted for
 *                 without anyone remembering to update a constant.
 */
export function lockHeightFor(
  viewportHeight: number,
  top: number,
  bottomPadding: number,
  chromeHeight = 0,
): number {
  const available = viewportHeight - top - bottomPadding - LOCK_GUTTER;
  const needed = chromeHeight + MIN_TABLE_HEIGHT;
  return Math.max(MIN_LOCK_HEIGHT, Math.round(Math.max(available, needed)));
}

/** Scrolling this far into the list hands the screen to the table. */
export const STATS_HIDE_AT = 24;

/** Coming back within this of the top gives the numbers back. */
export const STATS_SHOW_AT = 4;

/**
 * Should the statistics strip step aside because the operator is reading the
 * list? Returns the next state given the list's scroll position and the
 * current one.
 *
 * Harel, 2026-09-15: *"the table should be full page i think."* At rest the
 * strip is the headline and the list sits under it. The moment someone scrolls
 * into the list they have stopped looking at the headline, so the strip gets
 * out of the way and the table takes the whole screen. Scrolling back to the
 * top brings it back.
 *
 * 🪤 THE TWO THRESHOLDS ARE NOT A STYLE CHOICE. Hiding the strip makes the
 * table ~340px taller, which changes what is scrolled — so a single threshold
 * would hide the strip, re-measure, fall back under the line, show it again,
 * and flicker on every wheel tick. Hiding at 24px and only restoring within 4px
 * of the very top means the state cannot chatter across one boundary.
 *
 * Driven by the LIST's scroll rather than the page's on purpose: the table owns
 * its own scrollport, so a wheel over it never reaches the page. A page-driven
 * version would simply never fire for anyone whose cursor is on the rows.
 */
export function shouldHideStatsForScroll(scrollTop: number, hiddenNow: boolean): boolean {
  if (hiddenNow) return scrollTop > STATS_SHOW_AT;
  return scrollTop > STATS_HIDE_AT;
}

/**
 * Put the PAGE (not the list) back at the top.
 *
 * Walks up from the list to the first ancestor that actually scrolls
 * vertically, and resets it. Falls back to the window when nothing in between
 * is the scroller — the OPS shell scrolls in a div today, but that is an
 * implementation detail of the layout, not something this should assume.
 *
 * Deliberately NOT smooth: this fires on a wheel tick, and an animated scroll
 * racing the operator's own scrolling is how you get a page that fights back.
 */
export function scrollPageToTop(from: Element): void {
  for (let n = from.parentElement; n; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === "auto" || oy === "scroll") && n.scrollHeight > n.clientHeight) {
      n.scrollTop = 0;
      return;
    }
  }
  window.scrollTo(0, 0);
}
